import React, { ChangeEvent, Dispatch, FC, useCallback, useMemo, useReducer } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import {
  FormNodeLinkTermsAgreementModel,
  FormNodePIIModel,
  FormNodeTermsAgreementModel,
  FormNodeType,
  FormPageModel,
  FormSectionModel,
} from '../../../shared/kiosk';
import { OptionDataValue } from '../../../shared/options';
import { GenericOptionInput, ProductModel } from '../../../shared/orders';
import {
  ConRegistrationForm,
  RegistrationInfo,
  RegistrationUpsert,
  RegistrationUserInfo,
} from '../../../shared/registration/model';
import { AttendanceFlags, ProductOptionViewer } from '../../components';
import { FormNodeBadgeArtSelector } from '../../components/formElements/models/BadgeArtSelector';
import { FormNodeLinkTermsAgreement } from '../../components/formElements/models/FormNodeLinkTermsAgreement';
import { FormNodeMarkdown } from '../../components/formElements/models/FormNodeMarkdown';
import { FormNodePII } from '../../components/formElements/models/FormNodePII';
import { FormNodeTermsAgreement } from '../../components/formElements/models/FormNodeTermsAgreement';
import { ProfilePictureEditor } from '../../containers/account/profilePictureEditor';
import { mapProductAddons } from '../../containers/store/ProductConfigurator';
import { useTranslation } from '../../translations';
import { renderName, useForm, useUserSafe } from '../../utils';
import { generateRandomName } from '../../utils/nameGenerator';
import { cleanupOptions } from '../../utils/options';
import {
  FormActionType,
  FormContext,
  FormStateOptionConfig,
  FormStateRegistration,
  FormStateType,
} from '../form/FormReducer';
import { BadgePreview } from './BadgePreview';
import { EmergencyContactDynamic } from './EmergencyContactInfo';
import { RandomizeBadgeInfoButton } from './RandomizeBadge';

interface ConventionFormProps {
  readonly attendanceType: ProductModel;
  readonly registration?: RegistrationInfo;
  readonly user: RegistrationUserInfo;
  readonly regForm: ConRegistrationForm;
  readonly askForChildren?: boolean;
  readonly hasChildren?: boolean;
  readonly onSuccess: (reg: RegistrationUpsert) => Promise<void>;
}

function getRegistrationUpsert(state: FormStateRegistration): RegistrationUpsert {
  return {
    attendanceTypeId: state.attendanceTypeId,
    badgeName: state.badgeName,
    emergencyContactName1: state.emergencyContactName1,
    emergencyContactName2: state.emergencyContactName2,
    emergencyContactPhone1: state.emergencyContactPhone1,
    emergencyContactPhone2: state.emergencyContactPhone2,
    flags: state.enabledFlags,
    options: state.options,
    badgeArtId: state.badgeArtId,
    profilePicture: state.profilePicture,
  };
}

function hasOption(product: ProductModel | undefined, optionId: number): boolean {
  if (!product) {
    return false;
  }

  return (
    product.options.some((t) => t.id === optionId) ||
    product.addons.flatMap((t) => t.options).some((t) => t.id === optionId)
  );
}

// Check if
function isNewOption(
  products: ProductModel[],
  oldProductId: number,
  newProductId: number,
  optionId: number,
): boolean {
  const oldProduct = products.find((t) => t.id === oldProductId);
  const newProduct = products.find((t) => t.id === newProductId);

  if (hasOption(oldProduct, optionId)) {
    return false;
  }

  if (hasOption(newProduct, optionId)) {
    return true;
  }

  return false;
}

// eslint-disable-next-line max-lines-per-function
export const RegistrationDynamicInfoForm: FC<ConventionFormProps> = ({
  registration: conreg,
  attendanceType,
  user,
  regForm,
  onSuccess,
  hasChildren,
  askForChildren,
}) => {
  const ownerUser = useUserSafe();
  const isPaidAttendance = !!conreg?.paidOrderItem;
  const options = mapProductAddons(attendanceType.options, attendanceType.addons);
  const initialState = useMemo<FormStateRegistration>(() => {
    const stateReg: FormStateRegistration = {
      attendanceTypeId: attendanceType.id,
      badgeArtId: conreg?.badgeArtId ?? null,
      defaultBadgeArtId: conreg?.badgeArtId ?? null,
      badgeName: conreg?.badgeName ?? '',
      checkedAgreements: conreg ? ['conventionTerms'] : [],
      emergencyContactName1: conreg?.emergencyContactName1 ?? null,
      emergencyContactName2: conreg?.emergencyContactName2 ?? null,
      emergencyContactPhone1: conreg?.emergencyContactPhone1 ?? null,
      emergencyContactPhone2: conreg?.emergencyContactPhone2 ?? null,
      enabledFlags: conreg?.flags ?? [],
      hasChildren,
      options: cleanupOptions([...regForm.options, ...options], conreg?.options ?? {}),
      type: 'registration',
    };

    if (ownerUser.id !== user.id) {
      stateReg.emergencyContactName1 = renderName(ownerUser);
      stateReg.emergencyContactPhone1 = ownerUser.phone;
    }

    return stateReg;
  }, [attendanceType.id, conreg, hasChildren, regForm.options, options, ownerUser, user.id]);

  const reducer = useCallback(
    (old: FormStateRegistration, action: FormActionType) => {
      switch (action.type) {
        case 'updateOption': {
          return {
            ...old,
            options: {
              ...old.options,
              [action.optionId.toString()]: action.value,
            },
          } as FormStateRegistration;
        }

        case 'setProfilePicture': {
          return {
            ...old,
            profilePicture: action.file,
          } as FormStateRegistration;
        }

        case 'randomizeBadgeInfo': {
          const speciesOption = regForm.options.find(
            (t) => t.fromRegistration && t.type === 'text',
          );

          const species =
            (speciesOption && (old.options[speciesOption.id] as string | undefined)) ?? '';

          const random = generateRandomName({ badgeName: old.badgeName, species });
          return {
            ...old,
            badgeName: random.badgeName,
            options: speciesOption
              ? {
                  ...old.options,
                  [speciesOption.id]: random.species,
                }
              : old.options,
          };
        }

        case 'setEmergencyContact': {
          return {
            ...old,
            emergencyContactName1: action.name1 ?? old.emergencyContactName1,
            emergencyContactName2: action.name2 ?? old.emergencyContactName2,
            emergencyContactPhone1: action.phone1 ?? old.emergencyContactPhone1,
            emergencyContactPhone2: action.phone2 ?? old.emergencyContactPhone2,
          } as FormStateRegistration;
        }

        case 'updateFlags': {
          return {
            ...old,
            enabledFlags: action.flags,
          } as FormStateRegistration;
        }

        case 'setBadgeName': {
          return {
            ...old,
            badgeName: action.value ?? old.badgeName,
          } as FormStateRegistration;
        }

        case 'setPolicyChecked': {
          if (action.checked) {
            return {
              ...old,
              checkedAgreements: [...old.checkedAgreements, action.policy],
            } as FormStateRegistration;
          }

          return {
            ...old,
            checkedAgreements: old.checkedAgreements.filter((t) => t !== action.policy),
          } as FormStateRegistration;
        }

        case 'toggleChildren': {
          return {
            ...old,
            hasChildren: !old.hasChildren,
          } as FormStateRegistration;
        }

        case 'setBadgeArtId': {
          return {
            ...old,
            badgeArtId: action.badgeArtId,
          };
        }

        default: {
          throw new Error(`Unhandled action type: ${action.type}`);
        }
      }
    },
    [regForm],
  );

  const [formState, dispatch] = useReducer(reducer, initialState);

  const context = useMemo<FormContext>(() => {
    const allOptions: GenericOptionInput[] = [...regForm.options, ...attendanceType.options];

    for (const addon of attendanceType.addons) {
      for (const option of addon.options) {
        if (addon.isFree && !allOptions.some((t) => t.id === option.id)) {
          allOptions.push(option);
        }
      }
    }

    const optionConfig: Record<string, FormStateOptionConfig> = {};
    const speciesOption = regForm.options.find((t) => t.fromRegistration && t.type === 'text');

    for (const option of allOptions) {
      let disabled = false;

      if (isPaidAttendance) {
        if (option.type === 'currency') {
          disabled = true;
        }

        if (option.type === 'select' || option.type === 'multi') {
          disabled = !!option.selectValues?.some((t) => t.priceModifier > 0);
        }

        const isNew = isNewOption(
          regForm.products,
          conreg.paidOrderItem!.productId,
          attendanceType.id,
          option.id,
        );

        // We only enforce mutability if the attendance is paid.
        // But if the user upgrades from "basic" to "sponsor"
        // The user will have the option to select a t-shirt size
        if (!option.mutable && !isNew) {
          disabled = true;
        }
      }

      optionConfig[option.id] = {
        disabled,
      };
    }

    let other = [...allOptions];

    for (const section of regForm.formLayout.sections) {
      for (const node of section.nodes) {
        if (node.type === 'option') {
          other = other.filter((t) => t.id !== node.optionId);
        }
      }
    }

    return {
      regId: conreg?.id,
      badgeDesign: regForm.badgeDesign,
      flags: regForm.flags,
      options: regForm.options,
      allOptions,
      otherOptions: other,
      product: attendanceType,
      optionConfig,
      speciesOptionId: speciesOption?.id,
      user,
    };
  }, [
    regForm.options,
    regForm.badgeDesign,
    regForm.flags,
    regForm.products,
    regForm.formLayout.sections,
    attendanceType,
    conreg?.id,
    conreg?.paidOrderItem,
    user,
    isPaidAttendance,
  ]);

  const { ts } = useTranslation();

  const form = useForm(async () => {
    let policyChecked = true;
    const { checkedAgreements } = formState;

    for (const section of regForm.formLayout.sections) {
      for (const node of section.nodes) {
        if (node.type === 'termsAgreement' && !checkedAgreements.includes(node.termsKey)) {
          policyChecked = false;
        }

        if (node.type === 'linkTermsAgreement' && !checkedAgreements.includes('convention')) {
          policyChecked = false;
        }
      }
    }

    if (!policyChecked) {
      toast.error(ts('dynamic_reg_form.must_accept_policy'));
      return;
    }

    await onSuccess(getRegistrationUpsert(formState));
  }, [formState, onSuccess, regForm.formLayout.sections, ts]);

  return (
    <form className="w-100" onSubmit={form.onSubmit}>
      <Row className="justify-content-center">
        <Col lg={10} xs={12}>
          <Row id="registrationForm">
            <RegistrationPageRenderer
              askForChildren={askForChildren}
              context={context}
              dispatch={dispatch}
              formState={formState}
              page={regForm.formLayout}
            />
          </Row>
        </Col>
        <Col className="mt-10" lg={6} xs={12}>
          <Button block color="primary" disabled={form.saving} id="submitForm" type="submit">
            {ts(conreg ? 'update_registration' : 'common.register')}
          </Button>
        </Col>
      </Row>
    </form>
  );
};

interface RegistrationPageRendererProps {
  readonly page: FormPageModel;
  readonly context: FormContext;
  readonly formState: FormStateRegistration;
  readonly dispatch: Dispatch<FormActionType>;
  readonly askForChildren?: boolean;
}

const RegistrationPageRenderer: FC<RegistrationPageRendererProps> = ({
  page,
  context,
  formState,
  dispatch,
  askForChildren,
}) => {
  return page.sections.map((section) => {
    return (
      <RegistrationSectionRenderer
        askForChildren={askForChildren}
        context={context}
        dispatch={dispatch}
        formState={formState}
        key={section.id}
        section={section}
      />
    );
  });
};

interface RegistrationSectionRendererProps {
  readonly section: FormSectionModel;
  readonly context: FormContext;
  readonly formState: FormStateRegistration;
  readonly dispatch: Dispatch<FormActionType>;
  readonly askForChildren?: boolean;
}

const RegistrationSectionRenderer: FC<RegistrationSectionRendererProps> = ({
  section,
  context,
  formState,
  dispatch,
  askForChildren,
}) => {
  // Do not render empty blocks
  if (section.nodes.every((node) => isEmpty(node, context))) {
    return null;
  }

  return (
    <Col className="mb-10 badge-design" lg={section.size === 'large' ? 12 : 6} xs={12}>
      <Card>
        <CardHeader>{section.name}</CardHeader>
        <CardBody>
          {section.nodes.map((node) => {
            return (
              <RegistrationNodeRenderer
                askForChildren={askForChildren}
                context={context}
                dispatch={dispatch}
                formState={formState}
                key={node.id}
                node={node}
              />
            );
          })}
        </CardBody>
      </Card>
    </Col>
  );
};

function isEmpty(node: FormNodeType, context: FormContext): boolean {
  if (node.type === 'otherOptions') {
    return context.otherOptions.length === 0;
  }

  if (node.type === 'badgePreview') {
    // No badge design means no badge preview
    return !context.badgeDesign;
  }

  return false;
}

interface RegistrationNodeRendererProps {
  readonly node: FormNodeType;
  readonly context: FormContext;
  readonly formState: FormStateRegistration;
  readonly dispatch: Dispatch<FormActionType>;
  readonly askForChildren?: boolean;
}

const RegistrationNodeRenderer: FC<RegistrationNodeRendererProps> = ({
  node,
  context,
  formState,
  dispatch,
  askForChildren,
}) => {
  switch (node.type) {
    case 'text': {
      return node.text;
    }

    case 'randomizeButton': {
      return <RandomizeBadgeInfoButton dispatch={dispatch} />;
    }

    case 'option': {
      const option = context.allOptions.find((t) => t.id === node.optionId);

      if (!option) {
        if (node.optionId === 'badgeName') {
          return <BadgeNameInput dispatch={dispatch} state={formState} />;
        }

        return null;
      }

      return (
        <DynamicOptionRenderer
          context={context}
          dispatch={dispatch}
          option={option}
          state={formState}
        />
      );
    }

    case 'profilePicture': {
      return (
        <ProfilePictureInput
          dispatch={dispatch}
          ignoreGravatar={node.ignoreGravatar ?? false}
          title={node.title}
          user={context.user}
        />
      );
    }

    case 'divider': {
      return <hr />;
    }

    case 'badgePreview': {
      return <BadgePreview context={context} state={formState} />;
    }

    case 'emergencyContacts': {
      return (
        <EmergencyContactDynamic dispatch={dispatch} required={node.required} state={formState} />
      );
    }

    case 'otherOptions': {
      return (
        <DynamicOptionViewerList
          context={context}
          dispatch={dispatch}
          options={context.otherOptions}
          state={formState}
        />
      );
    }

    case 'attendanceFlags': {
      return (
        <AttendanceFlagsRenderer
          askForChildren={askForChildren}
          context={context}
          dispatch={dispatch}
          state={formState}
        />
      );
    }

    case 'linkTermsAgreement': {
      const linkTermsNode = node;
      return (
        <DynamicFormNodeLinkTermsAgreement
          dispatch={dispatch}
          node={linkTermsNode}
          state={formState}
        />
      );
    }

    case 'markdown': {
      const markdownNode = node;
      return <FormNodeMarkdown {...markdownNode} />;
    }

    case 'pii': {
      return <DynamicPIIEntry node={node} />;
    }

    case 'subheader': {
      throw new Error('Not implemented yet: "subheader" case');
    }

    case 'termsAgreement': {
      const termsNode = node;
      return (
        <DynamicFormNodeTermsAgreement dispatch={dispatch} node={termsNode} state={formState} />
      );
    }

    case 'oauthLogin': {
      throw new Error('Not implemented yet: "oauthLogin" case');
    }

    case 'submit': {
      return (
        <Button block className="form-node" color={node.buttonColor ?? 'primary'} type="submit">
          {node.buttonText ?? 'Submit'}
        </Button>
      );
    }

    case 'badgeArtSelector': {
      return (
        <FormNodeBadgeArtSelector
          {...node}
          badgeArtId={formState.badgeArtId}
          defaultBadgeArtId={formState.defaultBadgeArtId}
          registrationId={context.regId}
          setBadgeArtId={(badgeArtId) => {
            dispatch({ type: 'setBadgeArtId', badgeArtId });
          }}
          userId={context.user.id}
        />
      );
    }

    default: {
      // eslint-disable-next-line react/jsx-no-literals
      return <i>UnhandledNode: {JSON.stringify(node)}</i>;
    }
  }
};

interface BadgeNameInputProps {
  readonly state: FormStateType;
  readonly dispatch: Dispatch<FormActionType>;
}

const BadgeNameInput: FC<BadgeNameInputProps> = ({ state, dispatch }) => {
  const { ts } = useTranslation();
  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'setBadgeName', value: event.target.value });
    },
    [dispatch],
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (state.type !== 'registration') {
    return null;
  }

  return (
    <FormGroup>
      <Label for="badgeName">{ts('common.badge_name')}</Label>
      <Input
        id="badgeName"
        maxLength={32}
        name="badgeName"
        onChange={onChange}
        value={state.badgeName}
      />
    </FormGroup>
  );
};

interface AttendanceFlagsRendererProps {
  readonly state: FormStateType;
  readonly context: FormContext;
  readonly dispatch: Dispatch<FormActionType>;
  readonly askForChildren?: boolean;
}

const AttendanceFlagsRenderer: FC<AttendanceFlagsRendererProps> = ({
  state,
  context,
  dispatch,
  askForChildren,
}) => {
  const onUpdate = useCallback(
    (flags: number[]) => {
      dispatch({ type: 'updateFlags', flags });
    },
    [dispatch],
  );

  const toggleChildren = useCallback(() => {
    dispatch({ type: 'toggleChildren' });
  }, [dispatch]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (state.type !== 'registration' || !context.flags) {
    return null;
  }

  return (
    <FormGroup>
      <AttendanceFlags
        askForChildren={askForChildren}
        enabledFlags={state.enabledFlags}
        flags={context.flags}
        hasChildren={state.hasChildren}
        onUpdate={onUpdate}
        toggleChildren={toggleChildren}
      />
    </FormGroup>
  );
};

interface ProfilePictureInputProps {
  readonly user: RegistrationUserInfo;
  readonly title?: string;
  readonly ignoreGravatar: boolean;
  readonly dispatch: Dispatch<FormActionType>;
}

const ProfilePictureInput: FC<ProfilePictureInputProps> = ({
  user,
  dispatch,
  title,
  ignoreGravatar,
}) => {
  return (
    <Row className="justify-content-center text-center">
      <Col lg={12} xs={12}>
        <ProfilePictureEditor
          ignoreGravatar={ignoreGravatar}
          inputUser={user}
          onChange={(file) => {
            dispatch({ type: 'setProfilePicture', file });
          }}
          title={title}
        />
      </Col>
    </Row>
  );
};

interface DynamicOptionRendererProps {
  readonly option: GenericOptionInput;
  readonly state: FormStateType;
  readonly context: FormContext;
  readonly dispatch: Dispatch<FormActionType>;
}

const DynamicOptionRenderer: FC<DynamicOptionRendererProps> = ({
  context,
  option,
  state,
  dispatch,
}) => {
  const onOptionChange = useCallback(
    (id: number, value: OptionDataValue) => {
      dispatch({
        type: 'updateOption',
        optionId: id,
        value,
      });
    },
    [dispatch],
  );

  const optionState = state.options[option.id];
  const optionConfig = context.optionConfig[option.id];

  return (
    <ProductOptionViewer
      defaultValue={optionState}
      disabled={optionConfig?.disabled}
      onChange={onOptionChange}
      option={option}
    />
  );
};

interface DynamicOptionsViewerProps {
  readonly context: FormContext;
  readonly options?: GenericOptionInput[];
  readonly state: FormStateType;
  readonly dispatch: Dispatch<FormActionType>;
}

const DynamicOptionViewerList: FC<DynamicOptionsViewerProps> = ({
  state,
  options,
  dispatch,
  context,
}) => {
  if (!options) {
    return null;
  }

  return (
    <Row>
      {options.map((o) => {
        return (
          <Col key={o.id} lg={6} xs={12}>
            <DynamicOptionRenderer context={context} dispatch={dispatch} option={o} state={state} />
          </Col>
        );
      })}
    </Row>
  );
};

interface DynamicFormNodePIIProps {
  readonly node: FormNodePIIModel;
}
const DynamicPIIEntry: FC<DynamicFormNodePIIProps> = ({ node }) => {
  const piiNode = node;
  return <FormNodePII {...piiNode} />;
};

interface DynamicFormNodeLinkTermsAgreementProps {
  readonly node: FormNodeLinkTermsAgreementModel;
  readonly state: FormStateType;
  readonly dispatch: Dispatch<FormActionType>;
}

const DynamicFormNodeLinkTermsAgreement: FC<DynamicFormNodeLinkTermsAgreementProps> = ({
  node,
  state,
  dispatch,
}) => {
  const setPolicyChecked = useCallback(
    (checked: boolean) => {
      dispatch({ type: 'setPolicyChecked', checked, policy: 'convention' });
    },
    [dispatch],
  );

  return (
    <FormNodeLinkTermsAgreement
      {...node}
      checked={state.checkedAgreements.includes('convention')}
      setChecked={setPolicyChecked}
    />
  );
};

interface DynamicFormNodeTermsAgreementProps {
  readonly node: FormNodeTermsAgreementModel;
  readonly state: FormStateType;
  readonly dispatch: Dispatch<FormActionType>;
}

const DynamicFormNodeTermsAgreement: FC<DynamicFormNodeTermsAgreementProps> = ({
  node,
  state,
  dispatch,
}) => {
  const setPolicyChecked = useCallback(
    (checked: boolean) => {
      dispatch({ type: 'setPolicyChecked', checked, policy: node.termsKey });
    },
    [dispatch, node.termsKey],
  );

  return (
    <FormNodeTermsAgreement
      {...node}
      checked={state.checkedAgreements.includes(node.termsKey)}
      setChecked={setPolicyChecked}
    />
  );
};
